// polyfill for canvas#toBlob for terrible browsers that don't support this function
import "blueimp-canvas-to-blob";
import { expandRange } from "./panel";

/**
 * Expand a range of values into explict mapping values
 *
 * eg: {"4-6": "V"} -> {4: "V", 5: "V", 6: "V"}
 *
 * @param mapping The colour map to modify (in place)
 * @param key The range as a - separated string
 * @param value The value for the entire range
 * @returns {*} Also return the mapping
 */
export function parseRange(mapping, key, value) {
  for (let i of expandRange(key)) {
    mapping[i] = value;
  }
  return mapping;
}

/**
 * Turn a string into a 4 member array of Regex
 *
 * If the original string doesn't have at least 4 members, defaultPattern is substituted
 *
 * @param pattern String of / separated patterns for product, subProduct, geography and subGeography
 */
export function getPatterns(pattern, defaultPattern = "^.*$") {
  const defaultPatterns = Array.from(
    new Array(4),
    (val, index) => defaultPattern
  );
  const patterns = pattern.split("/").concat(defaultPatterns).slice(0, 4);
  return Array.from(patterns, p => new RegExp(p));
}

/**
 * Convert the JSON from the database into usable colour maps
 *
 * Expands range specified mappings as well as compiles the raw regex strings into patterns
 *
 * @param language The language to return the labels for
 * @param colourCurveJson The raw JSON string from the database records
 * @returns {{}}
 */
export function deserializeColourCurves(language, colourCurveJson) {
  const colourCurves = {};
  for (let c of JSON.parse(colourCurveJson)) {
    try {
      const mapping = {};
      Object.entries(c.value).forEach(([key, value]) => {
        if (key.indexOf("-") === -1) {
          mapping[Number.parseInt(key, 10)] = value;
        } else {
          parseRange(mapping, key, value);
        }
      });
      colourCurves[c.pk] = {
        pk: c.pk,
        pattern: getPatterns(c.pattern),
        label: c[language],
        map: mapping
      };
    } catch (error) {
      console.error(error);
    }
  }
  return colourCurves;
}

// Creates a colourized version of the given image and adds it to the panel's list of colourized images.
// Note that this method is asynchronous due to the way `canvas#toBlob` works.
export function colourizeImage(colourMap, imgUrl, onCompleteCallback) {
  var img = new Image();
  img.setAttribute("crossOrigin", "");
  img.src = imgUrl;
  if (img.complete) {
    _colourize(colourMap, img, onCompleteCallback);
  } else {
    img.onload = function () {
      _colourize(colourMap, img, onCompleteCallback);
    };
  }
}

function _colourize(colourMap, img, onCompleteCallback) {
  // Create a temporary canvas to hold our uncolourized image.
  var canvas = document.createElement("canvas");
  canvas.height = img.height;
  canvas.width = img.width;

  // Draw the uncolourized image onto the canvas
  var ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, img.width, img.height);

  // Extract the image data
  var imageData = ctx.getImageData(0, 0, img.width, img.height);
  // Create a new empty image data object of equal size to the original
  var newImageData = ctx.createImageData(img.width, img.height);

  // Iterate over the extracted image data
  for (var index = 0; index < imageData.data.length; index += 4) {
    var grayscaleValue = imageData.data[index];

    // Get the grayscale to colour mapping.
    // If we don't have a mapping, we will simply use the original value.
    var mapping = colourMap[grayscaleValue];
    if (mapping == null) {
      mapping = [
        grayscaleValue,
        imageData.data[index + 1],
        imageData.data[index + 2],
        255
      ];
    }

    newImageData.data[index] = mapping[0];
    newImageData.data[index + 1] = mapping[1];
    newImageData.data[index + 2] = mapping[2];
    newImageData.data[index + 3] = 255;
  }
  ctx.putImageData(newImageData, 0, 0);

  // Don't ask me why...`canvas#toBlob` is asynchronous. So we have to set a callback here
  // in order to finish constructing the colourized image.
  var blob = null;
  canvas.toBlob(imageBlob => {
    blob = imageBlob;

    // From the colourized image blob, create a temporary object URL that the panel will use
    // in order to display the colourized version.
    var tempImageUrl = (window.URL || window.webkitURL).createObjectURL(blob);

    onCompleteCallback(tempImageUrl);
  });
}
